import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { apiService } from '../services/apiService';
import { comparisonRows, errorMessage, preferenceLabels } from '../services/quoteUtils';
import { useAccount } from './Auth';

const fields = [
  ['part_number', 'Part Number'], ['manufacturer', 'Manufacturer'], ['quantity', 'Quantity'],
  ['currency', 'Currency'], ['unit_price', 'Unit Price'], ['extended', 'Extended'],
  ['lead_time', 'Lead Time'], ['condition', 'Condition'], ['country', 'COO'], ['date_code', 'DC'],
  ['rohs', 'RoHS'], ['certification', 'Certification'], ['packaging', 'Packaging'],
  ['moq', 'MOQ'], ['multiples', 'Multiples'], ['notes', 'Notes']
];
const suggestions = {
  condition: ['Factory Sealed', 'New', 'Refurbished', 'As-Is'],
  rohs: ['Yes', 'No', '5/6', 'UK', 'N/A'],
  certification: ['Manufacturer CoC', 'Company CoC', 'None', 'NA'],
  packaging: ['Ammo', 'Bulk', 'Cut Tape', 'Tape and Reel', 'Tray', 'Tube']
};
const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'CNY', 'JPY', 'AUD', 'CHF', 'HKD', 'INR', 'SGD'];
const formatNumber = value => typeof value === 'number'
  ? value.toLocaleString(undefined, { maximumFractionDigits: 10 }) : value ?? '—';

export default function QuoteComparison({ bom, row, locked = false, onChanged, onMutating }) {
  const account = useAccount();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [hideApi, setHideApi] = useState(false);
  const [hideAuthorized, setHideAuthorized] = useState(false);
  const [sort, setSort] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const inFlight = useRef(false);
  const requestVersion = useRef(0);
  const internal = ['owner', 'editor'].includes(bom.policy.role);
  const read = useCallback(async cursor => {
    const version = ++requestVersion.current;
    const result = await apiService.listQuotes(bom.bom_id, row.line_item_id, cursor);
    if (version !== requestVersion.current) return;
    setData(previous => cursor && previous ? {
      ...result, quotes: [...previous.quotes, ...result.quotes], pending: previous.pending
    } : result);
    return result;
  }, [bom.bom_id, row.line_item_id]);
  useEffect(() => {
    setData(null); setError('');
    read().catch(failure => setError(errorMessage(failure)));
    return () => { requestVersion.current += 1; };
  }, [read, bom.revision]);
  const rows = useMemo(() => comparisonRows(data?.quotes || [], { hideApi, hideAuthorized, sort }),
    [data, hideApi, hideAuthorized, sort]);
  const change = async (operation, values) => {
    if (locked || inFlight.current || !data) return false;
    inFlight.current = true; setBusy(true); onMutating?.(true); setError('');
    let saved = false;
    try {
      await apiService.mutateQuote(bom.bom_id, row.line_item_id, data.revision, operation, values);
      saved = true;
      setShowForm(false);
      await read();
      await onChanged?.();
      return true;
    } catch (failure) {
      setError(`${saved ? 'Quote change saved, but refreshing failed. ' : ''}${errorMessage(failure)}${!saved ? ' Reload quotes before retrying a conflict; your form is kept.' : ''}`);
      return false;
    } finally { inFlight.current = false; setBusy(false); onMutating?.(false); }
  };
  const openForm = () => {
    const source = aliases => {
      const column = bom.columns.find(c => aliases.includes(c.name.trim().toLowerCase()));
      return String(column ? row.data[column.id] ?? '' : '');
    };
    setForm({
      vendor_email: internal ? '' : account?.email || '', vendor_name: '', contact_name: '',
      part_number: source(['part', 'part number', 'mpn', 'manufacturer part number']),
      manufacturer: source(['manufacturer', 'mfr']), currency: 'USD', authorized: false,
      no_bid: false, price_breaks: [{ quantity: '', unit_price: '' }]
    });
    setShowForm(true);
  };
  const set = (name, value) => setForm(previous => ({ ...previous, [name]: value }));
  const field = ([name, label]) => <Form.Group key={name} controlId={`quote-${name}`}>
    <Form.Label>{label}</Form.Label>
    <Form.Control size="sm" maxLength={name === 'notes' ? 2000 : 240} value={form[name] || ''}
      list={suggestions[name] ? `quote-suggestions-${name}` : undefined}
      onChange={event => set(name, event.target.value)} />
    {suggestions[name] && <datalist id={`quote-suggestions-${name}`}>
      {suggestions[name].map(value => <option key={value} value={value} />)}
    </datalist>}
  </Form.Group>;
  return <div className="bomwise-quote-panel">
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
      <strong>Vendor quotes · line {row.order_index + 1}</strong>
      <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => {
        setBusy(true); setError(''); read().catch(failure => { setData(null); setError(errorMessage(failure)); }).finally(() => setBusy(false));
      }}>Reload quotes</Button>
      {data?.can_submit && <Button size="sm" className="ms-auto" disabled={locked || busy} onClick={openForm}>Add quote</Button>}
    </div>
    {error && <Alert variant="danger" className="py-2">{error}</Alert>}
    {locked && <p className="text-muted">Save or discard BOM edits before changing quotes.</p>}
    {!data && !error && <Spinner size="sm" aria-label="Loading quotes" />}
    {data && <>
      <div className="bomwise-quote-filters">
        <Form.Check label="Hide API quotes" checked={hideApi} onChange={event => setHideApi(event.target.checked)} />
        <Form.Check label="Hide authorized quotes" checked={hideAuthorized} onChange={event => setHideAuthorized(event.target.checked)} />
        <span>Preference groups first; sort within each group. Compare prices within the same currency.</span>
        {sort && <Button size="sm" variant="link" onClick={() => setSort(null)}>Default order</Button>}
      </div>
      <div className="bomwise-quote-table-scroll">
        <Table size="sm" className="bomwise-quote-table mb-0" aria-label="Quote comparison">
          <thead><tr>{fields.map(([name, label]) => <th key={name}
            aria-sort={sort?.field === name ? sort.direction === 1 ? 'ascending' : 'descending' : 'none'}>
            <button type="button" onClick={() => setSort(previous => ({
              field: name, direction: previous?.field === name ? -previous.direction : 1
            }))}>{label}{sort?.field === name ? sort.direction === 1 ? ' ↑' : ' ↓' : ''}</button>
          </th>)}</tr></thead>
          <tbody>{rows.map(quote => <tr key={quote.row_id} className={`bomwise-quote-${quote.no_bid ? 'no-bid' : quote.preference || 'unclassified'}`}>
            {fields.map(([name]) => <td key={name}>
              {name === 'part_number' ? <>
                <strong>{quote.part_number || '—'}</strong>
                <div className="bomwise-vendor-identity">{quote.vendor_name} · {quote.contact_name}<br />{quote.vendor_email}</div>
                {quote.no_bid ? <strong>No bid</strong> : data.can_categorize
                  ? <Form.Select size="sm" aria-label={`Preference for ${quote.vendor_name}, quantity ${quote.quantity}, ${quote.break_id}`}
                    disabled={locked || busy} value={quote.preference || 'unclassified'} onChange={event => change('preference', {
                      quote_id: quote.quote_id, break_id: quote.break_id, preference: event.target.value
                    })}>{Object.entries(preferenceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Form.Select>
                  : quote.preference && <div>{preferenceLabels[quote.preference]}</div>}
              </> : quote.no_bid && ['quantity', 'unit_price', 'extended'].includes(name) ? '—' : formatNumber(quote[name])}
            </td>)}
          </tr>)}
          {(data.pending || []).map(pending => <tr key={pending.vendor_email} className="bomwise-quote-pending">
            <td>{pending.vendor_email}</td><td colSpan={15}><strong>Awaiting response</strong></td>
          </tr>)}
          {!rows.length && !data.pending?.length && <tr><td colSpan={16}>{data.quotes.length ? 'No quotes match the visibility filters.' : 'No quotes for this line yet.'}</td></tr>}
          </tbody>
        </Table>
      </div>
      {data.next_cursor && <Button className="mt-2" size="sm" variant="outline-primary" disabled={busy} onClick={() => {
        setBusy(true); read(data.next_cursor).catch(failure => setError(errorMessage(failure))).finally(() => setBusy(false));
      }}>Load more quotes</Button>}
      {data.next_cursor && <p className="text-muted mb-0">Sorting applies to loaded price breaks. Load all pages for a complete comparison.</p>}
    </>}
    <Modal show={showForm} onHide={() => !busy && setShowForm(false)} size="xl" backdrop="static">
      <Form onSubmit={async event => {
        event.preventDefault();
        await change('submit', { ...form, price_breaks: form.no_bid ? [] : form.price_breaks });
      }}>
        <Modal.Header closeButton><Modal.Title>Add vendor quote · line {row.order_index + 1}</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {locked && <Alert variant="warning">Save or discard BOM edits before submitting this quote.</Alert>}
          <fieldset disabled={busy || locked}>
            <div className="bomwise-quote-context">
              {internal && <Form.Check className="mb-2" label="Authorized quote" checked={!!form.authorized}
                onChange={event => set('authorized', event.target.checked)} />}
              <div className="bomwise-quote-form-grid">
                {field(['vendor_name', internal ? 'Vendor company' : 'Your company'])}
                {field(['contact_name', internal ? 'Vendor contact name' : 'Your contact name'])}
                <Form.Group controlId="quote-vendor-email"><Form.Label>Vendor email</Form.Label>
                  <Form.Control size="sm" type="email" required readOnly={!internal} value={form.vendor_email || ''}
                    onChange={event => set('vendor_email', event.target.value)} /></Form.Group>
              </div>
              {!internal && <small>Your verified account email is used by the server. Only your own quotes are visible to you.</small>}
            </div>
            <div className="bomwise-quote-form-grid bomwise-quote-part">
              {fields.filter(([name]) => ['part_number', 'manufacturer', 'condition'].includes(name)).map(field)}
            </div>
            <div className="bomwise-quote-form-grid">
              {fields.filter(([name]) => ['lead_time', 'country', 'date_code', 'rohs', 'certification', 'packaging', 'moq', 'multiples'].includes(name)).map(field)}
            </div>
            <div className="d-flex gap-3 align-items-center my-3">
              <Form.Group controlId="quote-currency"><Form.Label>Currency</Form.Label>
                <Form.Select size="sm" value={form.currency || 'USD'} onChange={event => set('currency', event.target.value)}>
                  {currencies.map(currency => <option key={currency}>{currency}</option>)}
                </Form.Select></Form.Group>
              <Form.Check label="No bid" checked={!!form.no_bid} onChange={event => set('no_bid', event.target.checked)} />
            </div>
            {!form.no_bid && <>
              {(form.price_breaks || []).map((tier, index) => <div key={index} className="bomwise-price-break">
                {['quantity', 'unit_price'].map(name => <Form.Group key={name} controlId={`quote-break-${index}-${name}`}>
                  <Form.Label>{name === 'quantity' ? 'Quantity' : 'Unit price'}</Form.Label>
                  <Form.Control size="sm" type="number" step="any" min={name === 'quantity' ? '0.0000000001' : '0'} required
                    value={tier[name]} onChange={event => set('price_breaks', form.price_breaks.map((item, i) =>
                      i === index ? { ...item, [name]: event.target.value } : item))} />
                </Form.Group>)}
                <div><span>Extended ({form.currency})</span><output>{tier.quantity !== '' && tier.unit_price !== ''
                  ? formatNumber(Number(tier.quantity) * Number(tier.unit_price)) : '—'}</output></div>
                <Button size="sm" variant="outline-danger" aria-label={`Remove price break ${index + 1}`} disabled={form.price_breaks.length === 1}
                  onClick={() => set('price_breaks', form.price_breaks.filter((_, i) => i !== index))}>×</Button>
              </div>)}
              <Button size="sm" variant="outline-primary" disabled={(form.price_breaks?.length || 0) >= 25}
                onClick={() => set('price_breaks', [...form.price_breaks, { quantity: '', unit_price: '' }])}>Add price break</Button>
            </>}
            <Form.Group className="mt-3" controlId="quote-notes"><Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={2} maxLength={2000} value={form.notes || ''} onChange={event => set('notes', event.target.value)} /></Form.Group>
          </fieldset>
        </Modal.Body>
        <Modal.Footer><span className="text-muted me-auto">Each submission is preserved separately.</span>
          <Button type="submit" disabled={locked || busy || !form.vendor_name?.trim() || !form.contact_name?.trim()}>Save quote</Button></Modal.Footer>
      </Form>
    </Modal>
  </div>;
}
